import { Link } from "@inertiajs/react"
import { ClipboardList } from "lucide-react"

import { Card, CardContent } from "@/components/ui/card"
import { SectionHeader } from "@/components/ui/headers"
import { StatusBadge } from "@/components/ui/status-badge"
import { Button } from "@/components/ui/button"
import {
  INVENTORY_PATHS,
  suppliesNeedingAttention,
  supplyAlert,
  tInventory,
  type ShortageState,
  type Supply,
} from "@/features/staff/inventory/inventory"

/**
 * The one place that decides whether this restaurant may be told it is short.
 *
 * ## Two empties that need different sentences
 *
 * "No hay faltantes" and "no podemos saber si hay faltantes" look identical on
 * screen and mean opposite things. A restaurant with no recipe lines has no
 * writer of `recipe_consumption` at all: stock only ever goes up, so an empty
 * shortage list is not good news, it is the absence of a mechanism. Telling that
 * restaurant "todo en orden" is the failure
 * `config/location_features.yml` names when it ships inventory switched off —
 * *"a half-configured inventory reports shortages that are not real"* — with the
 * sign flipped.
 *
 * So while `shortage.reportable` is false this component renders **no figure at
 * all**: no count, no list, no reassuring green. It renders the setup prompt and
 * points at the screen that fixes it. The gate lives here rather than in the
 * workspace because it is the whole product rule, and a rule spread across three
 * render sites is a rule two of them will eventually forget.
 *
 * ## Above zero, coverage rather than a boolean
 *
 * "18 de 40 platillos tienen receta" is the honest reading, and it stays on
 * screen beside the shortages: a manager who can see that less than half the
 * menu is costed knows exactly how much of the list below to believe. Hiding it
 * once the first recipe exists would swap one confident lie for another.
 */
export function ShortageSummary({
  shortage,
  supplies,
}: {
  shortage: ShortageState
  supplies: Supply[]
}) {
  if (!shortage.reportable) {
    return (
      <Card data-testid="inventory-setup-prompt">
        <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
          <ClipboardList className="text-muted-foreground size-8" aria-hidden="true" />
          <p className="text-sm font-semibold">{tInventory("setup_title")}</p>
          <p className="text-muted-foreground max-w-md text-sm">{tInventory("setup_hint")}</p>
          <Button asChild data-testid="inventory-setup-recipes">
            <Link href={INVENTORY_PATHS.recipes}>{tInventory("setup_action")}</Link>
          </Button>
        </CardContent>
      </Card>
    )
  }

  const attention = suppliesNeedingAttention(supplies)

  return (
    <Card density="compact" data-testid="inventory-shortage">
      <CardContent className="space-y-4">
        <div className="space-y-1">
          <p className="text-muted-foreground text-xs">{tInventory("coverage_label")}</p>
          <p
            className="text-foreground text-sm font-semibold tabular-nums"
            data-testid="inventory-coverage"
          >
            {tInventory("coverage_value", { covered: shortage.covered, total: shortage.total })}
          </p>
          <p className="text-muted-foreground text-xs">{tInventory("coverage_hint")}</p>
        </div>

        <div className="space-y-2">
          <SectionHeader title={tInventory("shortage_title")} className="px-0" />

          {attention.length === 0 ? (
            <p className="text-muted-foreground text-sm" data-testid="inventory-shortage-empty">
              {tInventory("shortage_empty")}
            </p>
          ) : (
            <ul className="divide-border divide-y" data-testid="inventory-shortage-list">
              {attention.map((supply) => (
                <li
                  key={supply.id}
                  className="flex flex-wrap items-center justify-between gap-2 py-2"
                  data-testid={`inventory-shortage-${supply.id}`}
                >
                  <span className="min-w-0 truncate text-sm">{supply.name}</span>
                  <span className="flex items-center gap-2">
                    <span className="text-foreground text-sm font-semibold tabular-nums">
                      {supply.stockOnHand} {supply.unitLabel}
                    </span>
                    <StatusBadge
                      tone={supplyAlert(supply) === "short" ? "danger" : "warning"}
                      label={
                        supplyAlert(supply) === "short"
                          ? tInventory("short_badge")
                          : tInventory("low_badge")
                      }
                      data-testid={`inventory-shortage-badge-${supply.id}`}
                    />
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
